import { useEffect, useState } from "react";
import { ShieldCheck, Link2, Eye } from "lucide-react";
import { fetchTransparency } from "@/lib/api";
import { SimTag, timeAgo } from "@/components/shared";

const TYPE_COLORS = {
  BUY: "bg-[#14F195]/20 text-[#14F195]",
  SWAP: "bg-[#9945FF]/20 text-[#9945FF]",
  DISTRIB: "bg-[#00F0FF]/20 text-[#00F0FF]",
};

const ONCHAIN_POINTS = [
  {
    icon: ShieldCheck,
    title: "Program-Owned Vault",
    text: "At launch the treasury will live in an auditable Solana program-derived address — no team wallet custody, anyone can inspect balances on any explorer.",
  },
  {
    icon: Link2,
    title: "Jupiter Routed Execution",
    text: "Basket purchases will execute through Jupiter aggregator swaps, with every transaction signature published to this ledger via Helius webhooks.",
  },
  {
    icon: Eye,
    title: "Public Snapshot Proofs",
    text: "Eligibility snapshots and distribution batches will be hashed on-chain so every holder can independently verify their allocation math.",
  },
];

export default function Transparency() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchTransparency().then(setData).catch(() => {});
  }, []);

  return (
    <div data-testid="transparency-page" className="bg-[#07050d]">
      <section className="py-20 px-6 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-12">
            <span className="mono-font text-xs uppercase tracking-widest text-[#9945FF] font-semibold">100% On-Chain Verification</span>
            <h1 className="display-font uppercase text-3xl md:text-5xl text-white mt-2">Simulated Treasury Ledger</h1>
            <p className="text-sm text-[#b5aed0] mt-3">
              Every buy order and distribution batch is designed to be recorded on Solana with zero hidden custody. Below is example activity showing the format.
            </p>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            {[
              { label: "Treasury Value", value: `$${(data?.treasury_value_usd ?? 248950).toLocaleString()}`, testid: "tp-treasury" },
              { label: "Simulated Buys", value: data?.total_buys_demo ?? 96, testid: "tp-buys" },
              { label: "Distribution Batches", value: data?.total_distributions_demo ?? 48, testid: "tp-distribs" },
            ].map((c) => (
              <div key={c.label} data-testid={c.testid} className="glass-panel rounded-2xl p-6">
                <div className="text-[11px] text-[#9b93b8] uppercase tracking-wider">{c.label}</div>
                <div className="text-2xl font-bold text-white mono-font mt-1">{c.value}</div>
              </div>
            ))}
          </div>

          {/* Ledger */}
          <div className="glass-panel rounded-2xl overflow-hidden border border-white/10" data-testid="ledger-table">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-black/40">
              <span className="mono-font text-xs text-white font-medium">Recent Simulated Activity</span>
              <SimTag>Helius RPC Mock Feed</SimTag>
            </div>
            <div className="divide-y divide-white/5 mono-font text-xs">
              {(data?.ledger ?? []).map((entry, i) => (
                <div key={i} className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-2 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded font-bold ${TYPE_COLORS[entry.type]}`}>{entry.type}</span>
                    <span className="text-white">{entry.text}</span>
                  </div>
                  <div className="flex items-center gap-4 text-[#9b93b8]">
                    <span>Tx: {entry.tx}</span>
                    <span>{timeAgo(entry.timestamp)}</span>
                    <span className="text-[#14F195]">{entry.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-[#ffaa00] mono-font mt-4" data-testid="ledger-disclaimer">
            * All entries above are simulated. Transaction identifiers are mock values and do not exist on Solana.
          </p>
        </div>
      </section>

      {/* On-chain architecture */}
      <section className="py-24 px-6 bg-[#090612]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="mono-font text-xs uppercase tracking-widest text-[#14F195] font-semibold">Launch Architecture</span>
            <h2 className="display-font uppercase text-3xl md:text-5xl text-white mt-2">How This Works On-Chain</h2>
            <p className="text-sm text-[#a8a1c2] mt-3">The transparency stack planned for mainnet launch.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ONCHAIN_POINTS.map((p, i) => (
              <div key={p.title} data-testid={`onchain-point-${i + 1}`} className="glass-panel p-8 rounded-2xl d1-up" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="w-11 h-11 rounded-xl bg-[#14F195]/10 border border-[#14F195]/30 flex items-center justify-center mb-5">
                  <p.icon size={20} className="text-[#14F195]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{p.title}</h3>
                <p className="text-sm text-[#b5aed0] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
